// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Display-layer lifecycle for a plan approval.
 *
 * There is no `EXECUTING` state upstream, so it is derived from the execution
 * window. This is a **schedule** claim, not a dispatch claim — nothing here
 * confirms the site acted on the plan, so labels read "executing on schedule".
 *
 * Compares **instants**, deliberately unlike {@link executesTodayOrTomorrow},
 * whose day-key comparison would report a plan as executing all day.
 */

import { getStringAttr, parseExecutionStart } from './planAttributes';
import type { PlanAttributes } from './planAttributes';
import type { HitlApproval } from '../types';

export type PlanLifecycle = 'EXECUTING' | 'COMPLETED' | 'PENDING' | 'APPROVED' | 'REJECTED';

export interface ExecutionWindow {
  start: Date;
  end: Date;
}

/**
 * The approved execution window as instants, or undefined when either end is
 * missing or unparseable. `executionStart`/`executionEnd` carry a real offset
 * (`2026-08-06T00:00:00-04:00`), so no timezone is assumed;
 * {@link parseExecutionStart} anchors the bare `YYYY-MM-DD` form to UTC.
 */
export function getExecutionWindow(attributes: PlanAttributes): ExecutionWindow | undefined {
  const start = parseExecutionStart(getStringAttr(attributes, 'executionStart'));
  const end = parseExecutionStart(getStringAttr(attributes, 'executionEnd'));
  if (!start || !end || end.getTime() <= start.getTime()) return undefined;
  return { start, end };
}

/**
 * Derived lifecycle for an approval: `EXECUTING` while an approved plan's window
 * contains `now`, `COMPLETED` once it has closed, otherwise the raw state.
 *
 * Only `APPROVED` plans can execute — a rejected plan's window passing does not
 * make it completed, it stays rejected. The window is half-open (`start <= now <
 * end`) so a plan is never both executing and completed at the boundary.
 */
export function planLifecycle(approval: HitlApproval, now: Date = new Date()): PlanLifecycle {
  if (approval.state !== 'APPROVED') return approval.state;
  const window = getExecutionWindow(approval.attributes);
  if (!window) return 'APPROVED';
  const at = now.getTime();
  if (at >= window.end.getTime()) return 'COMPLETED';
  return at >= window.start.getTime() ? 'EXECUTING' : 'APPROVED';
}

/** True when the plan's approved execution window contains `now`. */
export function isExecuting(approval: HitlApproval, now: Date = new Date()): boolean {
  return planLifecycle(approval, now) === 'EXECUTING';
}
